/**
 * WeekView
 * CSS Grid week calendar with 08:00-18:00 range, 30-minute intervals.
 * Fetches appointments via Convex and renders them as coloured blocks.
 */
import { useMemo } from "react";
import {
  startOfWeek,
  addDays,
  isSameDay,
  format,
  isToday,
} from "date-fns";
import { de } from "date-fns/locale";
import { useQuery } from "convex/react";
import { api } from "@jourfx/convex/client";
import { Skeleton, TooltipProvider } from "@jourfx/ui";
import { AppointmentBlock } from "./AppointmentBlock";
import type { CalendarAppointment } from "./AppointmentBlock";
import { CurrentTimeIndicator } from "./CurrentTimeIndicator";

const START_HOUR = 8;
const END_HOUR = 18;
const SLOT_MINUTES = 30;
const TOTAL_SLOTS = ((END_HOUR - START_HOUR) * 60) / SLOT_MINUTES;

const DAY_LABELS = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"];

interface WeekViewProps {
  currentDate: Date;
  onSlotClick?: (timestamp: number) => void;
  gutachterId?: string;
}

export function WeekView({ currentDate, onSlotClick, gutachterId }: WeekViewProps) {
  const weekStart = startOfWeek(currentDate, { weekStartsOn: 1 });
  const weekDays = useMemo(
    () => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)),
    [weekStart],
  );

  const startDate = weekDays[0]!;
  const endDate = addDays(weekDays[6]!, 1);

  const appointments = useQuery(api.appointments.index.listByDateRange, {
    startDate: startDate.getTime(),
    endDate: endDate.getTime(),
    gutachterId: gutachterId || undefined,
  });

  // Bucket appointments by day index (0-6)
  const buckets = useMemo(() => {
    const map = new Map<number, CalendarAppointment[]>();
    if (!appointments) return map;
    for (const apt of appointments) {
      const aptDate = new Date(apt.scheduledAt);
      const idx = weekDays.findIndex((d) => isSameDay(d, aptDate));
      if (idx === -1) continue;
      const list = map.get(idx) ?? [];
      list.push(apt as unknown as CalendarAppointment);
      map.set(idx, list);
    }
    return map;
  }, [appointments, weekDays]);

  // Time labels
  const timeLabels = useMemo(() => {
    const labels: string[] = [];
    for (let i = 0; i <= TOTAL_SLOTS; i++) {
      const totalMinutes = START_HOUR * 60 + i * SLOT_MINUTES;
      const h = Math.floor(totalMinutes / 60)
        .toString()
        .padStart(2, "0");
      const m = (totalMinutes % 60).toString().padStart(2, "0");
      labels.push(`${h}:${m}`);
    }
    return labels;
  }, []);

  const isLoading = appointments === undefined;

  return (
    <TooltipProvider delayDuration={200}>
      <div className="overflow-auto rounded-lg border bg-card" data-testid="week-view-grid">
        {/* ── Header row ───────────────────────────────── */}
        <div
          className="grid border-b bg-muted/50"
          style={{ gridTemplateColumns: "4rem repeat(7, 1fr)" }}
        >
          {/* spacer for time column */}
          <div className="border-r px-2 py-3" />
          {weekDays.map((day, i) => {
            const today = isToday(day);
            return (
              <div
                key={i}
                className={`border-r px-2 py-3 text-center text-sm last:border-r-0 ${today ? "font-bold text-primary" : "text-muted-foreground"}`}
              >
                <span>{DAY_LABELS[i]}</span>{" "}
                <span className={today ? "rounded-full bg-primary px-1.5 py-0.5 text-primary-foreground" : ""}>
                  {format(day, "dd", { locale: de })}
                </span>
              </div>
            );
          })}
        </div>

        {/* ── Time grid ────────────────────────────────── */}
        <div
          className="relative grid"
          style={{ gridTemplateColumns: "4rem repeat(7, 1fr)" }}
        >
          {/* Time labels + gridlines */}
          <div className="relative border-r">
            {timeLabels.map((label, i) => (
              <div
                key={label}
                className="h-12 border-b px-1 text-right text-[11px] leading-none text-muted-foreground"
              >
                {i < timeLabels.length - 1 ? label : ""}
              </div>
            ))}
          </div>

          {/* Day columns */}
          {weekDays.map((day, dayIdx) => (
            <div
              key={dayIdx}
              className="relative border-r last:border-r-0"
            >
              {/* Grid rows (clickable empty slots) */}
              {Array.from({ length: TOTAL_SLOTS }, (_, slotIdx) => {
                const totalMinutes =
                  START_HOUR * 60 + slotIdx * SLOT_MINUTES;
                const slotDate = new Date(day);
                slotDate.setHours(
                  Math.floor(totalMinutes / 60),
                  totalMinutes % 60,
                  0,
                  0,
                );
                return (
                  <div
                    key={slotIdx}
                    className="h-12 border-b transition-colors hover:bg-accent/50"
                    onClick={() => onSlotClick?.(slotDate.getTime())}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        onSlotClick?.(slotDate.getTime());
                      }
                    }}
                    aria-label={`${format(slotDate, "EEEE dd.MM., HH:mm", { locale: de })} Uhr`}
                  />
                );
              })}

              {/* Appointment blocks */}
              {isLoading ? (
                <div className="absolute inset-0 flex flex-col gap-2 p-1 pt-2">
                  <Skeleton className="h-10 w-full rounded" />
                  <Skeleton className="h-16 w-full rounded" />
                </div>
              ) : (
                (buckets.get(dayIdx) ?? []).map((apt) => (
                  <AppointmentBlock
                    key={apt._id}
                    appointment={apt}
                    startHour={START_HOUR}
                    endHour={END_HOUR}
                  />
                ))
              )}

              {/* Current-time indicator (today only) */}
              {isToday(day) && (
                <CurrentTimeIndicator
                  startHour={START_HOUR}
                  endHour={END_HOUR}
                />
              )}
            </div>
          ))}
        </div>
      </div>
    </TooltipProvider>
  );
}
